import { useRef, useEffect, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

// OPTION 1: Glitch/Cyberpunk Effect - RGB Split with Scan Lines
export const CreditsOption1 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    if (isInView) {
      // Trigger glitch effect periodically
      const interval = setInterval(() => {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 200);
      }, 3000);
      
      // Initial glitch
      setTimeout(() => {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 500);
      }, 500);
      
      return () => clearInterval(interval);
    }
  }, [isInView]);

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 bg-[#050505] relative overflow-hidden"
      data-testid="credits-section-option1"
    >
      {/* Scan lines overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
        }}
      />
      
      {/* Flickering background */}
      <motion.div
        className="absolute inset-0 bg-[#E02128]/5"
        animate={glitchActive ? { opacity: [0, 0.3, 0, 0.2, 0] } : { opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <div className="flex flex-col items-center justify-center gap-8">
          {/* Glitch Text Container */}
          <div className="relative">
            {/* Main text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="font-display text-3xl md:text-5xl relative"
            >
              {/* RGB Split layers */}
              <span 
                className={`absolute inset-0 text-[#00ffff] ${glitchActive ? 'translate-x-[3px] translate-y-[-2px]' : ''}`}
                style={{ 
                  clipPath: glitchActive ? 'inset(20% 0 30% 0)' : 'none',
                  opacity: glitchActive ? 0.8 : 0,
                  transition: 'none'
                }}
              >
                <span className="font-bold">D</span>esigned and <span className="font-bold">D</span>eveloped by
              </span>
              <span 
                className={`absolute inset-0 text-[#ff0040] ${glitchActive ? 'translate-x-[-3px] translate-y-[2px]' : ''}`}
                style={{ 
                  clipPath: glitchActive ? 'inset(60% 0 10% 0)' : 'none',
                  opacity: glitchActive ? 0.8 : 0,
                  transition: 'none'
                }}
              >
                <span className="font-bold">D</span>esigned and <span className="font-bold">D</span>eveloped by
              </span>
              
              {/* Main visible text */}
              <span className="relative">
                <span className="text-[#E02128] font-bold">D</span>
                <span className="text-white">esigned</span>
                <span className="text-zinc-500"> and </span>
                <span className="text-[#E02128] font-bold">D</span>
                <span className="text-white">eveloped</span>
                <span className="text-zinc-500"> by</span>
              </span>
            </motion.div>
            
            {/* Glitch slice effect */}
            {glitchActive && (
              <motion.div
                className="absolute inset-0 bg-[#E02128]/20"
                style={{
                  clipPath: 'inset(40% 0 45% 0)',
                  transform: 'translateX(10px)',
                }}
              />
            )}
          </div>

          {/* Logo with glitch reveal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="relative"
          >
            <motion.div
              animate={glitchActive ? { x: [0, -5, 5, -3, 0] } : {}}
              transition={{ duration: 0.2 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_paddles-dyssc/artifacts/mj8530dq_GAMECORE-Logo-White.png" 
                alt="GameCore Logo" 
                className="h-16 md:h-24 object-contain"
                style={{
                  filter: glitchActive ? 'hue-rotate(90deg)' : 'none',
                  transition: 'filter 0.1s'
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Corner brackets - cyberpunk style */}
      <div className="absolute top-8 left-8 w-12 h-12 border-l-2 border-t-2 border-[#E02128]/50" />
      <div className="absolute top-8 right-8 w-12 h-12 border-r-2 border-t-2 border-[#E02128]/50" />
      <div className="absolute bottom-8 left-8 w-12 h-12 border-l-2 border-b-2 border-[#E02128]/50" />
      <div className="absolute bottom-8 right-8 w-12 h-12 border-r-2 border-b-2 border-[#E02128]/50" />
    </section>
  );
};

// OPTION 2: 3D Flip Character Reveal with Stagger
export const CreditsOption2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const text = [
    { char: 'D', isRed: true },
    { char: 'e', isRed: false },
    { char: 's', isRed: false },
    { char: 'i', isRed: false },
    { char: 'g', isRed: false },
    { char: 'n', isRed: false },
    { char: 'e', isRed: false },
    { char: 'd', isRed: false },
    { char: ' ', isRed: false },
    { char: 'a', isRed: false, isGray: true },
    { char: 'n', isRed: false, isGray: true },
    { char: 'd', isRed: false, isGray: true },
    { char: ' ', isRed: false },
    { char: 'D', isRed: true },
    { char: 'e', isRed: false },
    { char: 'v', isRed: false },
    { char: 'e', isRed: false },
    { char: 'l', isRed: false },
    { char: 'o', isRed: false },
    { char: 'p', isRed: false },
    { char: 'e', isRed: false },
    { char: 'd', isRed: false },
    { char: ' ', isRed: false },
    { char: 'b', isRed: false, isGray: true },
    { char: 'y', isRed: false, isGray: true },
  ];

  const charVariants = {
    hidden: { 
      rotateX: -90,
      opacity: 0,
      y: 50,
    },
    visible: (i) => ({
      rotateX: 0,
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: i * 0.04,
        ease: [0.215, 0.61, 0.355, 1],
      },
    }),
  };

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 bg-[#0B0B0C] relative overflow-hidden"
      data-testid="credits-section-option2"
      style={{ perspective: '1000px' }}
    >
      {/* Ambient light effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#E02128]/10 blur-[100px] rounded-full" />

      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <div className="flex flex-col items-center justify-center gap-10">
          {/* 3D Flip Text */}
          <div 
            className="font-display text-3xl md:text-5xl flex flex-wrap justify-center"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {text.map((item, i) => (
              <motion.span
                key={i}
                custom={i}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={charVariants}
                className={`inline-block ${
                  item.isRed ? 'text-[#E02128] font-bold' : 
                  item.isGray ? 'text-zinc-500' : 'text-white'
                }`}
                style={{ 
                  transformStyle: 'preserve-3d',
                  backfaceVisibility: 'hidden',
                }}
              >
                {item.char === ' ' ? '\u00A0' : item.char}
              </motion.span>
            ))}
          </div>

          {/* Logo with 3D entrance */}
          <motion.div
            initial={{ rotateY: -180, opacity: 0, scale: 0.5 }}
            animate={isInView ? { rotateY: 0, opacity: 1, scale: 1 } : {}}
            transition={{ 
              duration: 1,
              delay: 1.2,
              ease: [0.215, 0.61, 0.355, 1],
            }}
            className="relative"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Reflection effect */}
            <div className="absolute -bottom-16 left-0 right-0 h-16 overflow-hidden opacity-30">
              <img 
                src="https://customer-assets.emergentagent.com/job_paddles-dyssc/artifacts/mj8530dq_GAMECORE-Logo-White.png" 
                alt="" 
                className="h-20 md:h-24 object-contain mx-auto scale-y-[-1]"
                style={{ 
                  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
                  WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
                }}
              />
            </div>
            
            <img 
              src="https://customer-assets.emergentagent.com/job_paddles-dyssc/artifacts/mj8530dq_GAMECORE-Logo-White.png" 
              alt="GameCore Logo" 
              className="h-20 md:h-24 object-contain relative z-10"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// OPTION 3: Text Mask Reveal with Gradient Wipe (red stain removed)
export const CreditsOption3 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <section 
      ref={ref}
      className="py-12 md:py-16 bg-[#F9F9FA] relative overflow-hidden"
      data-testid="credits-section"
    >
      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <div className="flex flex-col items-center justify-center gap-6">
          {/* Text with gradient wipe reveal */}
          <div className="relative overflow-hidden">
            {/* Background text (gray) */}
            <div className="font-display text-3xl md:text-5xl text-[#E4E4E7]">
              <span className="font-bold">D</span>esigned and <span className="font-bold">D</span>eveloped by
            </div>
            
            {/* Revealed text with gradient mask */}
            <motion.div
              className="absolute inset-0 font-display text-3xl md:text-5xl"
              initial={{ clipPath: 'inset(0 100% 0 0)' }}
              animate={isInView ? { clipPath: 'inset(0 0% 0 0)' } : {}}
              transition={{ duration: 1.5, ease: [0.77, 0, 0.175, 1], delay: 0.3 }}
            >
              <span className="text-[#E02128] font-bold">D</span>
              <span className="text-[#111214]">esigned</span>
              <span className="text-[#A1A1AA]"> and </span>
              <span className="text-[#E02128] font-bold">D</span>
              <span className="text-[#111214]">eveloped</span>
              <span className="text-[#A1A1AA]"> by</span>
            </motion.div>

            {/* Moving gradient wipe line (clipped by the overflow-hidden wrapper) */}
            <motion.div
              className="absolute top-0 bottom-0 w-[3px]"
              initial={{ left: '0%' }}
              animate={isInView ? { left: '100%' } : {}}
              transition={{ duration: 1.5, ease: [0.77, 0, 0.175, 1], delay: 0.3 }}
              style={{
                background: 'linear-gradient(to bottom, transparent, #E02128, #E02128, transparent)',
              }}
            />
          </div>

          {/* Red line with logo hovering on it */}
          <div className="relative w-full flex flex-col items-center mt-4">
            {/* The red line */}
            <motion.div
              className="w-48 md:w-64 h-[2px] bg-gradient-to-r from-transparent via-[#E02128] to-transparent"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={isInView ? { scaleX: 1, opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 1.8 }}
            />
            
            {/* Logo hovering on the line */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.8,
                delay: 2.2,
                ease: "easeOut",
              }}
              className="relative -mt-8 md:-mt-10"
            >
              {/* Logo with minimal hover animation */}
              <motion.img 
                src="https://customer-assets.emergentagent.com/job_paddles-dyssc/artifacts/mj8530dq_GAMECORE-Logo-White.png" 
                alt="GameCore Logo" 
                className="h-16 md:h-20 object-contain relative z-10 brightness-0"
                animate={isInView ? { 
                  y: [0, -3, 0],
                } : {}}
                transition={{ 
                  duration: 3,
                  delay: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Default export showing all options for preview
export const CreditsSection = CreditsOption1;
